import { Checkbox, FormControlLabel } from '@mui/material';
import * as Sentry from "@sentry/react";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DISABLE_PAYMENTS_PATH, DISCOUNT_SIBLINGS_PATH, MODEL_COMPLEX_PATH, PACKAGES_PATH, studentsPath, userPackagesActive, userPackagesTemp } from "../../../functions/src/admin/paths";
import { demoUsers, PACKAGE_STATUS_ORDERED, PACKAGE_STATUS_PAID, PACKAGE_STATUS_TEMP } from "../../../functions/src/api/constants";
import { toDateFromServer } from "../../../functions/src/api/dates";
import { SCHOOL_DEMO } from "../../../functions/src/api/dbConfig";
import { calculatePackagePrices } from "../../../functions/src/common/packagePricing";
import { isRecipientBlockedFromPurchasingCatalog } from "../../../functions/src/common/packagePurchaseEligibility";
import type { CrossSchoolSiblingInfo } from "../../../functions/src/common/siblingDiscount";
import { checkCrossSchoolSiblingDiscount } from "../../../functions/src/common/siblingDiscount";
import { MealPackage, ModelComplexity, OrderPerson } from "../../../functions/src/types";
import { usePackageMenuPdfUrl } from "../../admin/adminPackages/packageMenuPdfUtils";
import { auth, currentDb, currentSchoolKey, DataSnapshot, getDatabase } from "../../api/firebase";
import { useDatabase, useDatabaseVal, useList } from "../../api/firebaseHooks";
import { createOrderPaymentWithRetry, placeOrderPackage, type PlaceOrderPackageResult, useBalance, warmPaymentCallables } from "../../api/funds";
import { getLangValue, langContext, localeFromLang } from "../../api/Lang";
import type { Lang as LangText } from "../../api/langConfig";
import LoadingSpinner from "../../api/LoadingSpinner";
import { useHasMultipleExchangeableMeals } from "../../api/mealUtils";
import OkDlg from "../../api/OkDlg";
import { TopUpProgressBelowLoader } from "../../api/progressBelowLoaders";
import { formatPriceEuro } from "../../api/priceFormat";
import { useServerNowMs } from "../../api/useServerNow";
import { useStrings } from "../../common-strings";
import { TwoButtonPopup } from "../../Order/OrderPopup";
import { useTopupStrings } from "../topup-strings";
import PackageWalletCreditsRow, { shouldShowPackageWalletCredits } from "./PackageWalletCreditsRow";
import { usePackagesStrings } from "./packages-strings";

export const DISABLE_PAYMENTS_VIVA_DISABLED = "vivaDisabled";
export const DISABLE_PAYMENTS_VIVA_DELAYED = "vivaDelayed";

export const PACKAGE_GENERAL_INFO_DEFAULTS: LangText[] = [
    {
        el: "Προσφέρουμε ένα κυρίως γεύμα την ημέρα, με σαλάτα, ψωμάκι και φρούτο",
        en: "We offer one main course per day, accompanied by a salad, bread and a fruit",
    },
    {
        el: "Επιτρέπεται αλλαγή γεύματος οποιαδήποτε ημέρα με επιλεγμένα πιάτα",
        en: "You may change your meal at any day with selected meals that we offer that day",
    },
    {
        el: "",
        en: "",
    },
];

export default function PackagePopup({ packageId, setDlgBuy, startStepNum, setPlacingOrder, setPlacingOrderPaymentPending }: PackagePopUpProps) {

    const { uid } = auth.currentUser;
    const userPackagesTempDb = useDatabase(userPackagesTemp(uid) || null);
    const userPackagesTempList = useList(userPackagesTempDb);
    const userPackagesActiveDb = useDatabase(userPackagesActive(uid) || null);
    const [mealPackage] = useDatabaseVal<MealPackage>(`${PACKAGES_PATH}/${packageId}` || null);

    const [dlgDemo, setDlgDemo] = useState(false);
    const [dlgVivaDisabled, setDlgVivaDisabled] = useState(false);
    const [packageActionErrorMsg, setPackageActionErrorMsg] = useState<string | null>(null);
    const [packageCopyPendingMsg, setPackageCopyPendingMsg] = useState<string | null>(null);
    /** After `placeOrderPackage` error, dismiss OkDlg then close buy flow (matches prior alert-then-close). Payment redirect errors only clear the dlg. */
    const closeBuyAfterPackageErrorDismissRef = useRef(false);

    const [stepNum, setStepNum] = useState(startStepNum ?? 1);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [loaded, setLoaded] = useState(false);

    const commonStrings = useStrings();
    const packagesStrings = usePackagesStrings();
    const topupStrings = useTopupStrings();
    const serverNowMs = useServerNowMs();
    const [creditsUsed, setCreditsUsed] = useState(0);
    const [payAmount, setPayAmount] = useState(0);
    const rbText = stepNum < 3 ? commonStrings.next : payAmount > 0 ? commonStrings.payPackage : commonStrings.completeOrder;

    // Boot prepare/finalize while the parent is still stepping through the package dialog.
    useEffect(() => {
        warmPaymentCallables('orderPackage');
    }, []);

    const [loadingSpinner, setLoadingSpinner] = useState(false);
    const [topUpRedirectPending, setTopUpRedirectPending] = useState(false);
    const [ordering, setOrdering] = useState(false);
    const [disablePayments] = useDatabaseVal<string>(DISABLE_PAYMENTS_PATH);
    const vivaPaymentsDisabled = disablePayments === DISABLE_PAYMENTS_VIVA_DISABLED;
    const vivaPaymentsDelayed = disablePayments === DISABLE_PAYMENTS_VIVA_DELAYED;

    // Check if there are multiple exchangeable meals available using shared utility
    const hasMultipleExchangeableMeals = useHasMultipleExchangeableMeals();

    useEffect(() => {
        if (loaded) {
            (async () => {
                try {
                    if (userPackagesTempDb?.ref) {
                        await userPackagesTempDb.ref.remove();
                    }
                } catch (err) {
                    Sentry.captureException(err);
                }
            })();
        }
    }, [loaded]);


    useEffect(() => {
        setLoaded(!!userPackagesTempDb);
    }, [userPackagesTempDb]);

    const onPackagesNextStep = async () => {
        if (stepNum === 1) {
            if (!isButtonDisabled) {
                setStepNum(2);
                setIsButtonDisabled(true);
            }
        }
        else if (stepNum === 2) {
            if (!isButtonDisabled)
                setStepNum(stepNum + 1);
        }
        else if (stepNum === 3) {
            // Block credit card in demo unless user is in demoUsers list (for testing)
            // NOTE: Backend also validates this - comment out backend validation in functions/src/index.ts to allow testing
            const isDemo = currentSchoolKey === SCHOOL_DEMO && !demoUsers.includes(uid);
            if (isDemo && payAmount > 0) {
                setDlgDemo(true);
                return;
            }
            if (vivaPaymentsDisabled === true && payAmount > 0) {
                setDlgVivaDisabled(true);
                return;
            }

            // Set package order status to ordered and proceed to handle payment to finalize order
            const tempPackages = userPackagesTempList.filter(p => p.child('status').val() === PACKAGE_STATUS_TEMP);

            if (serverNowMs === undefined) {
                setPackageActionErrorMsg(packagesStrings.serverTimeNotReady);
                return;
            }

            setPlacingOrder(true);
            // setDlgBuy(null);
            if (payAmount > 0) {
                // Store the price (negative creditsUsed) in the first package so OrderApp can access it after payment
                // NOTE: This price is ONLY used for logging/monitoring purposes in the backend.
                // The backend calculates its own price and uses that for the actual balance deduction (security: never trust frontend price).
                // If the stored price differs from backend calculation, a warning is logged but the backend price is always used.
                const priceToDeduct = -creditsUsed;

                try {
                    const orderedAt = serverNowMs;
                    let isFirst = true;
                    for (const pc of tempPackages) {
                        const updates: { status: number; orderedAt: number; price?: number } = {
                            status: PACKAGE_STATUS_ORDERED,
                            orderedAt,
                        };
                        // Store price in the first package (OrderApp uses the first one)
                        if (isFirst) {
                            updates.price = priceToDeduct;
                            isFirst = false;
                        }
                        await pc.ref.update(updates);
                    }
                } catch (err) {
                    console.error('Failed to update package status before payment:', err);
                    Sentry.captureException(err);
                    setPlacingOrder(false);
                    closeBuyAfterPackageErrorDismissRef.current = false;
                    setPackageActionErrorMsg(topupStrings.redirectToPaymentFailed);
                    return;
                }

                // Go to bank
                console.log(`Redirect to bank for payment: ${[payAmount]}`);
                setTopUpRedirectPending(true);
                setPlacingOrderPaymentPending?.(true);
                setLoadingSpinner(true);
                try {
                    // Package payments are purpose-tagged Viva orders (no plain top-up)
                    await createOrderPaymentWithRetry(payAmount, 'orderPackage', {
                        packageId: tempPackages[0]?.child('packageId').val() || packageId,
                    });
                    setLoadingSpinner(false);
                    // Close dialog and let OrderApp handle payment result
                    setDlgBuy(null);
                } catch (error: any) {
                    console.error('Failed to redirect to payment:', error);
                    setLoadingSpinner(false);
                    setPlacingOrder(false);

                    // Extract error message from Firebase Functions error
                    let errorMessage = topupStrings.redirectToPaymentFailed;
                    if (error?.code === 'functions/failed-precondition' || error?.code === 'failed-precondition') {
                        errorMessage = error.message || topupStrings.topUpNotAvailableInDemo;
                    } else if (error?.code === 'functions/internal' || error?.code === 'internal') {
                        errorMessage = topupStrings.redirectToPaymentFailed;
                    } else if (error?.message) {
                        errorMessage = error.message;
                    } else if (typeof error === 'string') {
                        errorMessage = error;
                    }

                    closeBuyAfterPackageErrorDismissRef.current = false;
                    setPackageActionErrorMsg(errorMessage);
                } finally {
                    setTopUpRedirectPending(false);
                    setPlacingOrderPaymentPending?.(false);
                }
            } else {
                // No payment needed - complete order directly without storing price in package
                // (onCompleteOrder will calculate and pass price directly to backend)
                try {
                    const orderedAt = serverNowMs;
                    for (const pc of tempPackages) {
                        await pc.ref.update({ status: PACKAGE_STATUS_ORDERED, orderedAt });
                    }
                } catch (err) {
                    console.error('Failed to update package status before completing order:', err);
                    Sentry.captureException(err);
                    setPlacingOrder(false);
                    closeBuyAfterPackageErrorDismissRef.current = false;
                    setPackageActionErrorMsg(topupStrings.packageOrderCompletionFailed);
                    return;
                }
                void onCompleteOrder();
            }
        }
    }

    const onCompleteOrder = async () => {

        // console.log("onCompleteOrder");
        if (ordering)
            return;

        if (serverNowMs === undefined) {
            setPackageActionErrorMsg(packagesStrings.serverTimeNotReady);
            return;
        }

        setLoadingSpinner(true);
        setOrdering(true);

        try {
            const userDate = serverNowMs;
            const price = -creditsUsed;
            const result = await placeOrderPackage({ packageId, userDate, price, localDbURL: currentDb.databaseURL });
            onOrderCompleted(result);
        } catch (error) {
            Sentry.captureException(error);
            onOrderCompleted({
                error:
                    (error instanceof Error ? error.message : "") || topupStrings.packageOrderCompletionFailed,
            });
        } finally {
            setOrdering(false);
            setLoadingSpinner(false);
        }
    }

    function onOrderCompleted(result: PlaceOrderPackageResult) {
        setPlacingOrder(false);
        if (result.error) {
            closeBuyAfterPackageErrorDismissRef.current = true;
            setPackageActionErrorMsg(result.error);
            return;
        }
        if (result.copyComplete === false) {
            setPackageCopyPendingMsg(result.message || packagesStrings.packageCopyFinishingByUs);
            return;
        }
        setDlgBuy(null);
    }

    function onPackagesPopupClosed() {
        const toRemove = userPackagesTempList?.filter(p => p.child('status')?.val() === PACKAGE_STATUS_TEMP) ?? [];
        void Promise.all(toRemove.map(pTemp => pTemp.ref.remove())).catch((err) => console.error('Error clearing temp packages on close:', err));
        closeBuyAfterPackageErrorDismissRef.current = false;
        setPackageActionErrorMsg(null);
        setDlgBuy(null);
    }

    const isRightButtonDisabled = isButtonDisabled || (stepNum === 3 && payAmount > 0 && vivaPaymentsDisabled === true);

    return <TwoButtonPopup rightButtonText={rbText} leftButtonText={commonStrings.cancel2} onRightButtonClick={onPackagesNextStep} onLeftButtonClick={onPackagesPopupClosed} isDisabled={isRightButtonDisabled} >
        {loadingSpinner && <LoadingSpinner belowLoader={topUpRedirectPending ? <TopUpProgressBelowLoader active /> : undefined} />}
        {dlgDemo && <OkDlg title={packagesStrings.demoNoCreditCardTitle} msg={packagesStrings.demoNoCreditCardMsg} onClose={() => setDlgDemo(false)} />}
        {dlgVivaDisabled && <OkDlg title={topupStrings.paymentPausedDlgTitle} msg={topupStrings.vivaDisabledLongBody} onClose={() => setDlgVivaDisabled(false)} />}
        {packageCopyPendingMsg !== null && (
            <OkDlg
                title={packagesStrings.packageRegisteredDlgTitle}
                msg={packageCopyPendingMsg}
                onClose={() => {
                    setPackageCopyPendingMsg(null);
                    setDlgBuy(null);
                }}
            />
        )}
        {packageActionErrorMsg !== null && (
            <OkDlg
                title={topupStrings.paymentErrorTitle}
                msg={packageActionErrorMsg}
                onClose={() => {
                    setPackageActionErrorMsg(null);
                    if (closeBuyAfterPackageErrorDismissRef.current) {
                        closeBuyAfterPackageErrorDismissRef.current = false;
                        setDlgBuy(null);
                    }
                }}
            />
        )}
        <div>
            <div className='package-steps'>
                <div className='package-steps-labels'>
                    <div className='step-text step-text--active'>{packagesStrings.infoAndTerms}</div>
                    <div className={`step-text${stepNum >= 2 ? ' step-text--active' : ''}`}>{packagesStrings.selectPeople}</div>
                    <div className={`step-text${stepNum >= 3 ? ' step-text--active' : ''}`}>{commonStrings.payment}</div>
                </div>
                <div className={`package-steps-track package-steps-track--step${stepNum}`} aria-hidden>
                    <div className='step-marker'>
                        <img src="/img-assets/check.svg" className='step-check' alt="" />
                    </div>
                    <div className='step-marker'>
                        {stepNum >= 2
                            ? <img src="/img-assets/check.svg" className='step-check' alt="" />
                            : <div className='step-circle' />}
                    </div>
                    <div className='step-marker'>
                        {stepNum >= 3
                            ? <img src="/img-assets/check.svg" className='step-check' alt="" />
                            : <div className='step-circle' />}
                    </div>
                </div>
            </div>

            <div className='step-separator'></div>
            {stepNum == 1 && <Step1Form mealPackage={mealPackage} setIsButtonDisabled={setIsButtonDisabled} hasMultipleExchangeableMeals={hasMultipleExchangeableMeals} />}
            {stepNum == 2 && <Step2Form userPackages={userPackagesTempDb} paidPackages={userPackagesActiveDb} mealPackage={mealPackage} packageId={packageId} setIsButtonDisabled={setIsButtonDisabled} />}
            {stepNum == 3 && <Step3Form userPackages={userPackagesTempDb} paidPackages={userPackagesActiveDb} mealPackage={mealPackage} setCreditsUsed={setCreditsUsed} setPayAmount={setPayAmount} />}
            {stepNum === 3 && payAmount > 0 && vivaPaymentsDisabled === true && (
                <div className='payment-warning'>
                    <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                        {packagesStrings.temporaryPaymentPause}
                    </div>
                    {topupStrings.vivaDisabledLongBody}
                </div>
            )}
            {stepNum === 3 && payAmount > 0 && vivaPaymentsDelayed === true && (
                <div className='payment-warning'>
                    <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                        {packagesStrings.walletUpdateDelay}
                    </div>
                    {topupStrings.vivaDelayedLongBody}
                </div>
            )}
        </div>
    </TwoButtonPopup>
}
type PackagePopUpProps = {

    packageId: string;
    setDlgBuy: (id: string) => void;
    startStepNum?: number
    setPlacingOrder: (id: boolean) => void;
    /** Parent full-screen spinner while popup is unmounted during Viva redirect (see Packages placingOrder). */
    setPlacingOrderPaymentPending?: (pending: boolean) => void;
}

function Step1Form({ mealPackage, setIsButtonDisabled, hasMultipleExchangeableMeals }: Step1FormProps) {

    const [lang] = useContext(langContext);
    const commonStrings = useStrings();
    const packagesStrings = usePackagesStrings();
    const menuPdfDisplayName = mealPackage?.menuPdfName?.[lang];
    const { url: menuPdfUrl, loading: menuPdfLoading } = usePackageMenuPdfUrl(lang, currentSchoolKey, menuPdfDisplayName);
    const packageGeneralInfo = mealPackage?.generalInfo as any;

    const defaultBullets = [
        getLangValue(PACKAGE_GENERAL_INFO_DEFAULTS[0], lang),
        hasMultipleExchangeableMeals ? getLangValue(PACKAGE_GENERAL_INFO_DEFAULTS[1], lang) : '',
        '',
    ];
    const hasAnyCustomBullet = [0, 1, 2].some((idx) => {
        const customBullet = packageGeneralInfo?.[idx]?.[lang];
        return typeof customBullet === 'string' && !!customBullet.trim();
    });
    const bulletsToShow = [0, 1, 2]
        .map((idx) => {
            const customBullet = packageGeneralInfo?.[idx]?.[lang];
            const trimmedCustom = typeof customBullet === 'string' ? customBullet.trim() : '';
            return hasAnyCustomBullet ? (trimmedCustom || defaultBullets[idx]) : defaultBullets[idx];
        })
        .filter((bullet) => !!bullet);

    return <div>
        <div className='step-title'>{mealPackage?.title?.[lang]}</div>
        <div className='annual-card-price-exp' style={{ fontSize: '15px' }}>
            {mealPackage?.start && typeof mealPackage.start === 'string' ? toDateFromServer(mealPackage.start).toLocaleDateString(localeFromLang(lang)) : 'N/A'} - {mealPackage?.end && typeof mealPackage.end === 'string' ? toDateFromServer(mealPackage.end).toLocaleDateString(localeFromLang(lang)) : 'N/A'}
        </div>
        <div >
            <ul className='step-key-points'>
                {bulletsToShow.map((bullet, idx) => (
                    <li key={`general-info-${idx}`} style={{ marginTop: '4px' }}>
                        {bullet}
                    </li>
                ))}
                {/* <li style={{ marginTop: '4px' }}><Lang>Σε περίπτωση που το σχολείο αναγκαστεί να κλείσει λόγω νέου lock-down, θα επιστραφεί στον λογαριασμό σας στο Lunchbox το ποσό που σας αντιστοιχεί|In case physical access of the students is prohibited due to a new lock-down, we will reimburse the corresponding amount to your Lunchbox account.</Lang></li> */}
            </ul>
        </div>
        {!menuPdfLoading && menuPdfUrl && (
            <div style={{ marginBottom: '32px', marginLeft: '30px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src="/img-assets/pdf2.svg" style={{ width: '25px', height: '25px' }} alt="" />
                <a
                    href={menuPdfUrl}
                    download={menuPdfDisplayName}
                    style={{ fontSize: '15px', fontWeight: 500, color: '#262626', textDecoration: 'underline' }}
                >
                    {commonStrings.downloadInfo}
                </a>
            </div>
        )}
        <div className='step-terms'>
            <FormControlLabel
                control={
                    <Checkbox
                        onChange={e => setIsButtonDisabled(!e.target.checked)}
                        color="primary"
                    />
                }
                label={
                    <span>
                        {packagesStrings.acceptTermsPrefix}{" "}
                        <a href="/terms/terms-packages.html" target="_blank" style={{ fontSize: '18px', fontWeight: 'bold', color: '#262626' }}>
                            {packagesStrings.termsAndConditions}
                        </a>
                    </span>
                }
            />
        </div>
    </div>
}
type Step1FormProps = {
    mealPackage: MealPackage;
    setIsButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    hasMultipleExchangeableMeals: boolean;
}

function Step2Form({ userPackages, paidPackages, mealPackage, packageId, setIsButtonDisabled }: Step2FormProps) {

    const { uid } = auth.currentUser;
    const serverNowMs = useServerNowMs();
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const students = useDatabase(studentsPath(uid));
    const studentsList: DataSnapshot<OrderPerson>[] = useList(students);
    const isPackageForStaff = typeof mealPackage?.priceStaff !== 'number';

    const userPaidOrCompletedPackagesList = useList(paidPackages).filter(l => l.child('status').val() >= PACKAGE_STATUS_PAID);
    const tempPackages = useList(userPackages).filter(tp => tp.child('status').val() === PACKAGE_STATUS_TEMP);

    const paidRows = useMemo(() => {
        return userPaidOrCompletedPackagesList.map((pp) => ({
            recipient: pp.child('recipient').val() as string,
            start: pp.child('start').val() as string,
            end: pp.child('end').val() as string
        }));
    }, [userPaidOrCompletedPackagesList]);

    const packagesStrings = usePackagesStrings();

    useEffect(() => {
        if (tempPackages?.length > 0)
            setIsButtonDisabled(false);
        else
            setIsButtonDisabled(true);
    }, [tempPackages]);

    const onCheckedChanged = (studentId: string, student: OrderPerson, checked: boolean) => {
        const tempPackage = tempPackages?.find(tp => tp.child('recipient').val() === studentId);

        if (checked && !tempPackage) {
            // Add temp package with only essential fields
            userPackages.ref.push({
                packageId: packageId,
                recipient: studentId,
                status: PACKAGE_STATUS_TEMP,
                startedAt: serverNowMs ?? Date.now(),
                hasSide: !!student?.personnel ? false : true,
                hasDessert: !!student?.personnel ? false : true
            });
        } else if (!checked && tempPackage) {
            // Remove temp package
            tempPackage.ref.remove();
        }
    };

    const onSaladChange = (studentId: string, checked: boolean) => {
        const tempPackage = tempPackages?.find(tp => tp.child('recipient').val() === studentId);
        tempPackage?.ref.child('hasSide').set(checked);
    };

    const onDessertChange = (studentId: string, checked: boolean) => {
        const tempPackage = tempPackages?.find(tp => tp.child('recipient').val() === studentId);
        tempPackage?.ref.child('hasDessert').set(checked);
    };

    return <div style={{ marginTop: '40px' }}>

        <div className='step-terms' style={{ letterSpacing: '0.32px', marginBottom: '40px' }}>{packagesStrings.chooseRecipients}</div>
        {studentsList.map((person) => {
            const personData = person.val();
            const isStaff = !!personData?.personnel;
            const personName = `${personData.firstName} ${personData.lastName}`;
            // const personId = isStaff ? uid : person.key;
            const personId = person.key;

            if (
                personId &&
                isRecipientBlockedFromPurchasingCatalog(personId, mealPackage, paidRows)
            ) {
                return null;
            }

            // Don't show staff users if package doesn't have priceStaff
            if (isStaff && isPackageForStaff) {
                return null;
            }

            return (
                <div key={personId}>
                    <div className='name-box' style={{ width: 'inherit', minWidth: '200px' }}>
                        <div className='rowbox' style={{ alignItems: 'center' }}>
                            <div className='step-terms' style={{ letterSpacing: '0.32px', marginRight: '30px', paddingLeft: '4px' }}> {personName}</div>
                            <div className='step-terms' style={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                    onChange={(e) => { onCheckedChanged(personId, personData, e.target.checked) }}
                                    color="primary"
                                />
                            </div>
                        </div>
                        {tempPackages.filter(tp => tp.child('recipient').val() === personId)[0]?.exists() &&
                            <div className='package-side-check-lbl'>
                                <div >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled={true}
                                                checked={true}
                                                color="primary"
                                                size="small"
                                                sx={{ padding: '4px' }}
                                            />
                                        }
                                        label={<span style={{ fontSize: '14px' }}>{packagesStrings.meal}</span>}
                                        sx={{ marginLeft: 0 }}
                                    />
                                </div>
                                <div >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled={!modelConstants?.allowPackageSide}
                                                defaultChecked={true}
                                                onChange={(e) => { onSaladChange(personId, e.target.checked) }}
                                                color="primary"
                                                size="small"
                                                sx={{ padding: '4px' }}
                                            />
                                        }
                                        label={<span style={{ fontSize: '14px' }}>{packagesStrings.salad}</span>}
                                        sx={{ marginLeft: 0 }}
                                    />
                                </div>
                                <div >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled={!modelConstants?.allowPackageDessert}
                                                defaultChecked={true}
                                                onChange={(e) => { onDessertChange(personId, e.target.checked) }}
                                                color="primary"
                                                size="small"
                                                sx={{ padding: '4px' }}
                                            />
                                        }
                                        label={<span style={{ fontSize: '14px' }}>{packagesStrings.dessert}</span>}
                                        sx={{ marginLeft: 0 }}
                                    />
                                </div>
                            </div>
                        }
                    </div>
                </div>
            );
        })}

    </div >
}

type Step2FormProps = {
    userPackages: DataSnapshot;
    paidPackages: DataSnapshot;
    mealPackage: MealPackage;
    packageId: string;
    setIsButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

function Step3Form({ userPackages, paidPackages, mealPackage, setCreditsUsed, setPayAmount }: Step3FormProps) {

    const [lang] = useContext(langContext);
    const packagesStrings = usePackagesStrings();
    const { uid } = auth.currentUser;
    const paidPackagesList = useList(paidPackages).filter(l => l.child('status').val() >= PACKAGE_STATUS_PAID);
    const userPackagesList = useList(userPackages).filter(l => l.child('status').val() < PACKAGE_STATUS_PAID);
    const samePackages = paidPackagesList.filter(sp => sp.child('start').val() === mealPackage.start && sp.child('end').val() === mealPackage.end);
    const students: DataSnapshot<OrderPerson> = useDatabase(studentsPath(uid));
    const discountSiblings: number = useDatabase(DISCOUNT_SIBLINGS_PATH)?.val() || 0;
    const walletLabel = packagesStrings.walletLabel;

    const balance = useBalance();
    // Lunchbox credits are applied mandatorily (checkbox checked + disabled)
    const creditsSelected = balance > 0;

    // Check for cross-school sibling discount using shared async function
    const [crossSchoolSiblingInfo, setCrossSchoolSiblingInfo] = useState<CrossSchoolSiblingInfo | null>(null);
    const [siblingDiscountVerifyFailed, setSiblingDiscountVerifyFailed] = useState(false);
    useEffect(() => {
        let cancelled = false;
        async function run() {
            if (!mealPackage) {
                setCrossSchoolSiblingInfo(null);
                setSiblingDiscountVerifyFailed(false);
                return;
            }
            try {
                const info = await checkCrossSchoolSiblingDiscount(currentSchoolKey, mealPackage, uid, (url) => getDatabase(url));
                if (!cancelled) {
                    setCrossSchoolSiblingInfo(info);
                    setSiblingDiscountVerifyFailed(false);
                }
            } catch (err) {
                Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
                    tags: { component: 'PackagePopup.Step3Form', operation: 'checkCrossSchoolSiblingDiscount' },
                });
                if (!cancelled) {
                    setCrossSchoolSiblingInfo(null);
                    setSiblingDiscountVerifyFailed(true);
                }
            }
        }
        void run();
        return () => { cancelled = true; };
    }, [currentSchoolKey, mealPackage, uid]);

    // Calculate prices using shared utility function
    const { totalPrice: calculatedPrice, packagePrices } = useMemo(() => {
        // Convert DataSnapshot objects to plain objects for shared function
        const studentsMap: { [key: string]: OrderPerson } = {};
        if (students) {
            students.forEach((studentSnap) => {
                const studentId = studentSnap.key;
                if (studentId) {
                    studentsMap[studentId] = studentSnap.val() as OrderPerson;
                }
            });
        }

        return calculatePackagePrices({
            userPackagesList: userPackagesList.map(pkg => ({ recipient: pkg.child('recipient').val() })),
            students: studentsMap,
            mealPackage,
            samePackages: samePackages.map(pkg => ({ recipient: pkg.child('recipient').val() })),
            discountSiblings,
            crossSchoolSiblingInfo
        });
    }, [userPackagesList, students, mealPackage, samePackages, discountSiblings, crossSchoolSiblingInfo]);

    // Update parent component state in useEffect (temp packages only contain essential fields)
    useEffect(() => {
        // Update parent component state
        const creditsUsed = calculatedPrice < balance ? -calculatedPrice : -balance;
        const totalPayment = creditsSelected ? calculatedPrice + creditsUsed : calculatedPrice;
        setPayAmount(totalPayment);
        setCreditsUsed(creditsUsed);
    }, [calculatedPrice, balance, creditsSelected]);

    return <div>


        <div className='step-terms' style={{ letterSpacing: '0.32px', marginTop: '40px' }}>{mealPackage?.title?.[lang]}</div>
        {siblingDiscountVerifyFailed && (
            <div className="payment-alert-rectangle" style={{ marginTop: '16px' }}>
                <img src='/img-assets/alert2.svg' className="payment-alert-img" alt="" />
                <div className='await-pay-subtitle'>
                    <strong>{packagesStrings.siblingDiscountVerifyFailedTitle}</strong>
                    <div style={{ marginTop: '8px' }}>{packagesStrings.siblingDiscountVerifyFailedMsg}</div>
                </div>
            </div>
        )}
        <div className='offer-rectangle'>
            {(() => {
                let arr = [];
                for (let i = 0; i < userPackagesList.length; i++) {
                    const userPackage = userPackagesList[i];
                    const recipientId = userPackage.child('recipient').val();

                    // Determine if this package is for staff or student
                    const recipientData: OrderPerson = students?.child(recipientId).val();
                    const recipientIsStaff = !!recipientData?.personnel;

                    if (!students || !recipientData)
                        return;

                    // Use the already calculated price from packagePrices (match by index since order is preserved)
                    const packagePriceData = packagePrices[i];
                    const packagePrice = packagePriceData?.price || 0;

                    // Calculate display information
                    const packPrice = recipientIsStaff ? mealPackage?.priceStaff : mealPackage?.priceStudent;
                    const isDiscounted = packagePrice < packPrice;
                    const hasCrossSchoolSibling = crossSchoolSiblingInfo?.hasSiblingInOtherSchool;

                    arr.push(
                        <div className='offer-row' key={recipientData.firstName || 'staff'}>
                            <div>{recipientData.firstName} {recipientData.lastName}</div>
                            <div style={{ color: '#3C3C3C' }}>
                                {isDiscounted && <span className='offer-discounted-price'>{packPrice}€</span>}
                                {typeof packagePrice === "number" ? formatPriceEuro(packagePrice, lang) : ""}
                                {isDiscounted && hasCrossSchoolSibling && (
                                    <div style={{ fontSize: '12px', color: '#01ABAE', marginTop: '2px' }}>
                                        {packagesStrings.siblingDiscount}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                    // if (userPackagesList.length > i + 1)
                    arr.push(<div className='offer-price-separator' key={'Middle' + i + 'Sep'} />);
                    // else if (userPackagesList.length === i + 1)
                    // arr.push(<div className='offer-price-total-separator' key={'TotalSep'} />);
                }
                const creditsUsed = calculatedPrice < balance ? -calculatedPrice : -balance;
                const totalPayment = creditsSelected ? calculatedPrice + creditsUsed : calculatedPrice;
                const showWalletCredits = shouldShowPackageWalletCredits(balance ?? 0, calculatedPrice);

                if (showWalletCredits) {
                    const balanceAfterRemain = calculatedPrice < balance ? balance - calculatedPrice : 0;
                    arr.push(
                        <PackageWalletCreditsRow
                            key="useWallet"
                            label={walletLabel}
                            creditsUsed={creditsUsed}
                            balanceAfterRemain={balanceAfterRemain}
                            remainLabel={packagesStrings.willRemainPrefix}
                            compactAlign
                        />
                    );
                }
                arr.push(<div className='offer-price-total-separator' key={'TotalSep'} />);
                arr.push(
                    <div className='offer-row' style={{ justifyContent: 'right' }} key={'Total'}>
                        {packagesStrings.totalLabel}
                        <span style={{ fontWeight: 'bold', marginLeft: '12px' }}>{formatPriceEuro(Number(totalPayment ?? 0), lang)}</span>
                    </div>
                )

                return arr;
            })()}
        </div>
    </div>
}
type Step3FormProps = {
    userPackages: DataSnapshot;
    paidPackages: DataSnapshot;
    mealPackage: MealPackage;
    setCreditsUsed: React.Dispatch<React.SetStateAction<number>>;
    setPayAmount: React.Dispatch<React.SetStateAction<number>>;
}